import { Fragment, useMemo, useState } from 'react'

import { Alert, Button, Empty, Spin } from 'antd'

import { CheckCircleFilled, ReloadOutlined } from '@ant-design/icons'

import { ITableViewData } from '@services/data-service'

import { convertData } from '@/pages/editor/InsertData/helpers'

import { ColumnGroup } from './repeatColumnsPlan'
import styles from './RepeatColumns.module.css'

export const strategies = [
  {
    value: null,
    title: 'Slide per Column',
    description: "One new slide for each column that isn't on this slide yet."
  },
  {
    value: 'by_group_inclusion',
    title: 'Slide per Column in Group',
    description:
      "Every column in the group(s) this slide uses, one slide each — even ones you didn't pick."
  },
  {
    value: 'by_parent_group',
    title: 'Slide per Group',
    description: "One slide per group, with all its columns together. Ignores what's on this slide."
  }
]

export const plural = (count: number) => (count === 1 ? 'slide' : 'slides')

type Props = {
  table: ITableViewData | null
  loading: boolean
  groups: ColumnGroup[] | null
  slides: number[][] | null
  picked: number[]
  rows: number[]
  shortShapes: number
  strategy: string | null
  exact: boolean
  onUseGroups: () => void
}

const RepeatColumnsPreview = ({
  table,
  loading,
  groups,
  slides,
  picked,
  rows,
  shortShapes,
  strategy,
  exact,
  onUseGroups
}: Props) => {
  const [run, setRun] = useState(0)
  const values = useMemo(() => convertData(table), [table])

  if (!slides) {
    return (
      <div className={styles.preview}>
        {loading ? (
          <Spin />
        ) : (
          <Empty description="Add data to a chart or table on this slide to see a preview." />
        )}
      </div>
    )
  }

  const leaves = table.headers[table.headers.length - 1]
  const shownRows = rows.slice(0, 4)
  const hasGroupRow = table.headers.length > 1
  const buildRow = shownRows.length + (hasGroupRow ? 3 : 2)
  const step = Math.min(80, 800 / slides.length)
  const isPicked = (column: number) => picked.includes(column)
  const isUsed = (group: ColumnGroup) => group.columns.some(isPicked)
  const inSlides = new Set(slides.flat())
  const tone = (column: number) =>
    isPicked(column) ? styles.current : inSlides.has(column) ? styles.new : styles.unused
  const isIdle = (group: ColumnGroup) =>
    group.columns.every(column => tone(column) === styles.unused)
  const span = (columns: number[]) => `${columns[0] + 2} / span ${columns.length}`
  const title = strategies.find(option => option.value === strategy)?.title
  const skipped = strategy === null && !exact ? picked : []
  const idleGroups = strategy === 'by_group_inclusion' ? groups.filter(isIdle) : []
  const pickedNames = picked.map(column => leaves[column]?.name).join(', ')
  const groupNames = groups
    .filter(isUsed)
    .map(group => group.name)
    .filter(Boolean)
    .join(', ')

  const subtitle = exact ? (
    'Only the columns you listed get their own slide.'
  ) : strategy === null ? (
    <>
      This slide shows <b>{pickedNames}</b>. Every other column gets its own slide.
    </>
  ) : strategy === 'by_group_inclusion' ? (
    <>
      This slide uses <b>{groupNames || 'one group'}</b>. Every column in that group gets its own
      slide.
    </>
  ) : (
    'Each group becomes one slide, with all of its columns side by side.'
  )

  const emptyReason = exact
    ? 'None of the column indexes you entered are in this table.'
    : strategy === null
    ? 'Every column of this table is already on the slide.'
    : 'No columns are picked on this slide.'

  return (
    <div className={styles.preview}>
      <div className={styles.bar}>
        <div>
          <strong>
            Preview · {slides.length} new {plural(slides.length)}
          </strong>
          <div className={styles.muted}>{subtitle}</div>
        </div>
        <span className={styles.muted}>{title}</span>
        <Button size="small" icon={<ReloadOutlined />} onClick={() => setRun(run + 1)}>
          Replay
        </Button>
      </div>
      <div className={styles.stage}>
        <div
          className={styles.grid}
          style={{ gridTemplateColumns: `110px repeat(${leaves.length}, minmax(72px, 1fr))` }}
        >
          {hasGroupRow && (
            <>
              <div />
              {groups.map(group => (
                <div
                  key={group.columns[0]}
                  className={`${styles.group} ${isIdle(group) ? styles.unused : ''}`}
                  style={{ gridColumn: `span ${group.columns.length}` }}
                >
                  {group.name}
                </div>
              ))}
            </>
          )}
          <div />
          {leaves.map((leaf, column) => (
            <div key={column} className={`${styles.head} ${tone(column)}`} title={leaf.name}>
              {isPicked(column) && <CheckCircleFilled />}
              {leaf.name}
            </div>
          ))}
          {shownRows.map(row => (
            <Fragment key={row}>
              <div className={styles.rowHead}>{table.index[row]}</div>
              {leaves.map((_, column) => (
                <div key={column} className={`${styles.cell} ${tone(column)}`}>
                  {values[row]?.[column]}
                </div>
              ))}
            </Fragment>
          ))}
          <Fragment key={`${strategy}:${slides}:${run}`}>
            {slides.map((columns, index) => (
              <Fragment key={columns[0]}>
                <div
                  className={styles.arrow}
                  style={{
                    gridRow: buildRow,
                    gridColumn: span(columns),
                    animationDelay: `${index * step}ms`
                  }}
                >
                  {columns.length > 1 && <span className={styles.bracket} />}
                </div>
                <div
                  className={styles.slide}
                  style={{
                    gridRow: buildRow + 1,
                    gridColumn: span(columns),
                    animationDelay: `${200 + index * step}ms`
                  }}
                >
                  <div className={styles.slideTitle}>Slide {index + 1}</div>
                  <div
                    className={styles.slideBody}
                    style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
                  >
                    {columns.map(column => (
                      <div key={column} className={styles.slideHead}>
                        {leaves[column].name}
                      </div>
                    ))}
                    {shownRows.map(row =>
                      columns.map(column => (
                        <div key={`${row}-${column}`}>{values[row]?.[column]}</div>
                      ))
                    )}
                  </div>
                </div>
              </Fragment>
            ))}
            {skipped.map(column => (
              <Fragment key={`skipped-${column}`}>
                <div
                  className={styles.dashed}
                  style={{ gridRow: buildRow, gridColumn: column + 2 }}
                />
                <div
                  className={styles.skipped}
                  style={{ gridRow: buildRow + 1, gridColumn: column + 2 }}
                >
                  Skipped
                  <small>already on slide</small>
                </div>
              </Fragment>
            ))}
            {idleGroups.map(group => (
              <div
                key={`idle-${group.columns[0]}`}
                className={styles.idle}
                style={{ gridRow: buildRow + 1, gridColumn: span(group.columns) }}
              >
                No slides
                <small>nothing picked in this group</small>
              </div>
            ))}
          </Fragment>
        </div>
        {strategy === 'by_parent_group' && (
          <p className={styles.note}>
            What's on the current slide is ignored — this option always gives the same slides.
          </p>
        )}
        {shortShapes > 0 && (
          <Alert
            className={styles.alert}
            type="info"
            showIcon
            message={`${shortShapes} other ${
              shortShapes === 1 ? 'shape' : 'shapes'
            } on this slide will be left empty on some or all new slides`}
            description="Shapes with fewer columns to repeat than the one shown here run out before the last new slide."
          />
        )}
        {slides.length === 0 && (
          <Alert
            className={styles.alert}
            type="warning"
            showIcon
            message={`${title} won't create any slides`}
            description={`${emptyReason} Slide per Group makes one slide for each column group instead.`}
            action={
              strategy !== 'by_parent_group' && (
                <Button size="small" onClick={onUseGroups}>
                  Use Slide per Group
                </Button>
              )
            }
          />
        )}
      </div>
      <div className={styles.legend}>
        <span>
          <i className={styles.current} />
          On the current slide
        </span>
        <span>
          <i className={styles.newSwatch} />
          New slide
        </span>
        <span>
          <i className={styles.unusedSwatch} />
          Not used
        </span>
      </div>
    </div>
  )
}

export default RepeatColumnsPreview
